import { calendarDayInZone, endOfDayInZone, hasDayShape, isCalendarDay, instantFromWallClock, wallClockInZone } from '../zonedTime';

/**
 * 🔒 The Deal's timing (piece 18; decision 0719): the three dates the Intent
 * card's Timing row carries — when the quote is expected, when the work is
 * expected to start and when it is expected to finish. Each may be left empty.
 * The Expected quote is a calendar day, `YYYY-MM-DD`, with no clock and no zone.
 * 🔒 The start and the finish take an optional time of day (decision 2292): each
 * is a calendar day where no time was given, else the instant, written in ISO.
 */
export interface DealTimingDates {
  expectedQuoteDate: string | null;
  expectedStartDate: string | null;
  expectedFinishDate: string | null;
}

/** The three dates in the order the row draws them, each with the word the row names it by. */
export const DEAL_TIMING_FIELDS = [
  { key: 'expectedQuoteDate', label: 'Expected quote' },
  { key: 'expectedStartDate', label: 'Expected start' },
  { key: 'expectedFinishDate', label: 'Expected finish' },
] as const satisfies readonly { key: keyof DealTimingDates; label: string }[];

const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;

/** Which end of the work a timing value names: a day with no time stands at its start for the start and at its end for the finish. */
export type DealTimingEnd = 'start' | 'finish';

/** Whether a start or finish value carries a time of day, rather than a calendar day alone. */
export function dealTimingHasTime(value: string | null): boolean {
  return value !== null && !hasDayShape(value);
}

/**
 * 🔒 The instant a start or finish value stands for (decision 2292): a day with
 * no time is that day's start, or its end for the finish, in the zone given,
 * which is the Organization's on the server.
 */
export function dealTimingInstant(value: string, end: DealTimingEnd, zone: string): Date {
  if (hasDayShape(value)) return end === 'start' ? instantFromWallClock(`${value}T00:00`, zone) : endOfDayInZone(value, zone);
  return new Date(value);
}

/** 🔒 A stored start or finish back to its value: the day where the instant is that day's own boundary in the zone, else the instant (decision 2292). */
export function dealTimingValue(instant: Date | null, end: DealTimingEnd, zone: string): string | null {
  if (!instant) return null;
  const day = calendarDayInZone(instant, zone);
  return dealTimingInstant(day, end, zone).getTime() === instant.getTime() ? day : instant.toISOString();
}

/** A timing value as the note and a refusal write it: the day, or the day and the clock in the zone given. */
export function dealTimingText(value: string, zone: string): string {
  return hasDayShape(value) ? value : wallClockInZone(value, zone).replace('T', ' ');
}

/** The date a refusal is about, and the words that name it. */
export interface DealTimingFault {
  key: keyof DealTimingDates;
  message: string;
}

/**
 * Why the dates cannot be saved, and which date the refusal is about, or null
 * where they can. 🔨 An Expected quote after the Expected start and an Expected
 * finish before the Expected start are each refused by name, so the person reads
 * which date to move (decision 5491); the card asks this before it sends and the
 * server asks it whatever is sent. A date that is not a calendar day is refused
 * too. The quote is a day, so it is compared with the day the start falls on in
 * the zone given, and a quote on the start's own day is taken.
 */
export function dealTimingFault(dates: DealTimingDates, zone = 'UTC'): DealTimingFault | null {
  for (const field of DEAL_TIMING_FIELDS) {
    const value = dates[field.key];
    if (value === null) continue;
    const timed = field.key !== 'expectedQuoteDate' && INSTANT.test(value);
    if (timed ? Number.isNaN(Date.parse(value)) || !isCalendarDay(value.slice(0, 10)) : !isCalendarDay(value)) {
      return { key: field.key, message: field.key === 'expectedQuoteDate' ? `${field.label} is a calendar day, written year-month-day` : `${field.label} is a calendar day, with a time of day or without one` };
    }
  }
  const { expectedQuoteDate: quote, expectedStartDate: start, expectedFinishDate: finish } = dates;
  if (quote !== null && start !== null && quote > (hasDayShape(start) ? start : calendarDayInZone(start, zone))) {
    return { key: 'expectedQuoteDate', message: `the Expected quote ${quote} is after the Expected start ${dealTimingText(start, zone)}; move one of them` };
  }
  if (start !== null && finish !== null && dealTimingInstant(finish, 'finish', zone) < dealTimingInstant(start, 'start', zone)) {
    return { key: 'expectedFinishDate', message: `the Expected finish ${dealTimingText(finish, zone)} is before the Expected start ${dealTimingText(start, zone)}; move one of them` };
  }
  return null;
}

/** The refusal's words alone, which the server sends and the card reads. */
export function dealTimingProblem(dates: DealTimingDates, zone = 'UTC'): string | null {
  return dealTimingFault(dates, zone)?.message ?? null;
}

/** 🔒 The row completes when an Expected quote is set (decision 0719). */
export function dealTimingComplete(dates: DealTimingDates): boolean {
  return dates.expectedQuoteDate !== null;
}

/** Whether two sets of dates are the same: saving the dates already standing changes nothing. */
export function sameDealTiming(a: DealTimingDates, b: DealTimingDates): boolean {
  return DEAL_TIMING_FIELDS.every((field) => a[field.key] === b[field.key]);
}

/**
 * The dates written out, which is the note the stage gate reads on the Timing
 * row: *Expected quote 2026-10-01 · Expected start 2026-11-02*, the empty ones
 * left out, or blank where none is set.
 */
export function dealTimingSummary(dates: DealTimingDates, zone = 'UTC'): string {
  return DEAL_TIMING_FIELDS.filter((field) => dates[field.key] !== null).map((field) => `${field.label} ${dealTimingText(dates[field.key]!, zone)}`).join(' · ');
}

/**
 * The note a save writes: the new summary where the note is blank or still
 * reads the previous summary, and the note as it stands otherwise. 🔨 A note a
 * person typed before the row changed shape is kept, the rule the Budget row
 * follows (decision 0664). An empty summary clears a note that was only ever
 * the previous one.
 */
export function dealTimingNoteAfter(currentNote: string | null, previous: DealTimingDates, next: DealTimingDates, zone = 'UTC'): string | null {
  const note = currentNote?.trim() ?? '';
  const before = dealTimingSummary(previous, zone);
  if (note === '' || (before !== '' && note === before)) return dealTimingSummary(next, zone) || null;
  return currentNote;
}

/** The typed note the card reads beneath the dates, or null where the note is the dates' own summary or blank. */
export function typedDealTimingNote(currentNote: string | null, standing: DealTimingDates, zone = 'UTC'): string | null {
  const note = currentNote?.trim() ?? '';
  if (note === '') return null;
  if (note === dealTimingSummary(standing, zone)) return null;
  return currentNote;
}
